import { useState } from 'react';
import { useI18n } from '../i18n';
import { useAccount } from '../account';
import { useGuessGame } from '../lib/useGuessGame';
import { distanceKm, bearing, type Country } from '../lib/countries';
import { distanceColor } from '../lib/clue';
import GuessInput from '../components/GuessInput';
import Legend from '../components/Legend';
import EndBanner from '../components/EndBanner';
import ClassicCompetitive from './ClassicCompetitive';
import { ClassicBoard, type Row, type Verdict } from './classicBoard';
import { GuessSkeleton } from '../components/Skeleton';

/** 数值比较。任一方缺数据标 unknown —— 拿 0 去比会给出错误方向(朝鲜/梵蒂冈没有人均 GDP)。 */
function cmp(g: number | null | undefined, a: number | null | undefined) {
  if (g == null || a == null) return { hit: false, higher: false, unknown: true };
  return { hit: g === a, higher: g < a };
}

function localVerdict(c: Country, a: Country): Verdict {
  const km = distanceKm(c, a);
  return {
    isHit: c.cca3 === a.cca3,
    regionHit: c.region === a.region,
    pop: cmp(c.population, a.population),
    area: cmp(c.area, a.area),
    borders: cmp((c.borders || []).length, (a.borders || []).length),
    gdpPc: cmp(c.gdpPc || null, a.gdpPc || null),
    temp: cmp(c.tempC, a.tempC),
    distKm: km,
    bearing: Math.round(bearing(c, a)),
    distColor: distanceColor(km),
  };
}

// 本地 Classic:无尽模式 / 后端离线时的练习(不计排名)。答案前端确定性算出。
function ClassicLocal({ endless, practice }: { endless: boolean; practice?: boolean }) {
  const { t } = useI18n();
  const g = useGuessGame('classic', { endless });
  const a = g.answer;
  const done = g.status !== 'playing';
  const rows: Row[] = g.guesses.map((c, i) => ({
    country: c,
    verdict: localVerdict(c, a),
    last: i === g.guesses.length - 1,
  }));

  return (
    <div>
      {practice && <p className="practice-note">{t.offlinePractice}</p>}
      {!done && (
        <>
          <GuessInput onGuess={g.guess} />
          {endless && (
            <div className="skip-row">
              <button onClick={g.skip}>{t.skip}</button>
            </div>
          )}
        </>
      )}

      <ClassicBoard rows={rows} />

      {g.guesses.length > 0 && <Legend numbers />}
      {g.guesses.length === 0 && g.status === 'playing' && <p className="empty-hint">{t.emptyHint}</p>}

      {done && <EndBanner mode="classic" g={g} />}
    </div>
  );
}

// 入口:无尽=本地;每日+后端在线=竞技(服务器权威);每日+离线=本地练习。
export default function ClassicMode({ endless = false }: { endless?: boolean }) {
  const { me, loading } = useAccount();
  const [offline, setOffline] = useState(false);

  if (endless) return <ClassicLocal endless />;
  if (loading) return <GuessSkeleton />;
  if (me && me.uid && !offline) return <ClassicCompetitive onOffline={() => setOffline(true)} />;
  return <ClassicLocal endless={false} practice />;
}
